import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useInstitution } from '../contexts/InstitutionContext'
import { api } from '../services/api'
import { Navigate } from 'react-router-dom'
import { preserveInstitutionParams } from '../utils/navigation'

interface RoleBasedRouteProps {
  children: React.ReactNode
  allowedRoles: string[]
  redirectTo?: string
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ 
  children, 
  allowedRoles, 
  redirectTo = '/' 
}) => {
  const { currentUser } = useAuth()
  const { institution, theme, applyTheme } = useInstitution()
  const [userRole, setUserRole] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  // Apply institution theming
  useEffect(() => {
    applyTheme()
  }, [theme, applyTheme])

  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        setIsLoading(false)
        return
      }

      try {
        const roleData = await api.auth.getRole()
        setUserRole(roleData.role)
        setHasAccess(allowedRoles.includes(roleData.role))
      } catch (error) {
        console.error('Error checking user role:', error)
        setHasAccess(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAccess()
  }, [currentUser, allowedRoles])

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    )
  }

  if (!currentUser) {
    return <Navigate to={preserveInstitutionParams("/login")} />
  }

  if (!hasAccess) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        {institution?.logo_url && (
          <img 
            src={institution.logo_url} 
            alt={institution.name || 'Institution'} 
            style={{
              maxHeight: '80px',
              marginBottom: '1rem'
            }}
          />
        )}
        <h2>Access Denied</h2>
        <p>You don't have permission to access this platform{institution ? ` at ${institution.name}` : ''}.</p>
        <p>Your current role: <strong>{userRole}</strong></p>
        <p>Required roles: <strong>{allowedRoles.join(', ')}</strong></p>
        <button 
          onClick={() => window.location.href = redirectTo}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: theme.primary_color,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Go to Main Platform
        </button>
      </div>
    )
  }

  return <>{children}</>
}

export default RoleBasedRoute 